import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useSettings } from './SettingsContext';
import { tokens, breakpoints as bpValues } from '../styles/design-system';

// Build color scheme objects from design system tokens (used by charts and legacy consumers)
const colorSchemes = {
  light: {
    background: tokens.light.colorBg,
    text: tokens.light.colorText,
    cardBackground: tokens.light.colorSurface,
    border: tokens.light.colorBorder,
    primary: tokens.light.colorPrimary,
    secondary: '#6c757d',
    buttonText: '#ffffff',
    tableHeader: tokens.light.colorTableHeader,
    inputBackground: tokens.light.colorInputBg,
    badgeBackground: tokens.light.colorSurface,
    overlay: tokens.light.colorOverlay,
    
    // Enhanced colors
    success: '#28a745',
    warning: '#ffc107',
    danger: '#dc3545',
    info: '#17a2b8',
    
    // Responsive spacing scales
    spacing: {
      xs: '0.25rem',
      sm: '0.5rem',
      md: '1rem',
      lg: '1.5rem',
      xl: '2rem',
      xxl: '3rem'
    },
    
    // Responsive breakpoints (in pixels)
    breakpoints: {
      mobile: '768px',
      tablet: '992px',
      desktop: '1200px'
    },
    
    // Sidebar specific
    sidebar: {
      bg: '#1e293b',
      bgLighter: '#334155',
      bgDarker: '#0f172a',
      border: 'rgba(255,255,255,0.1)',
      text: '#e2e8f0',
      active: '#60a5fa',
      hover: 'rgba(96, 165, 250, 0.1)',
      width: '280px',
      mobileWidth: '260px'
    },
    
    // Footer specific
    footer: {
      bg: '#1e293b',
      border: '#334155',
      text: '#94a3b8',
      active: '#cbd5e1',
      hover: 'rgba(147, 197, 253, 0.7)',
      height: '60px',
      mobileHeight: '50px'
    },
    
    // Header specific
    header: {
      bg: '#1e293b',
      bgGradient: 'linear-gradient(90deg, #1e293b 0%, #0f172a 100%)',
      border: 'rgba(255,255,255,0.1)',
      text: '#e2e8f0',
      icon: '#60a5fa',
      height: '64px',
      mobileHeight: '56px'
    },
    
    // Chart colors
    charts: {
      primary: '#007bff',
      secondary: '#6c757d',
      success: '#28a745',
      danger: '#dc3545',
      warning: '#ffc107',
      info: '#17a2b8'
    },
    
    // Status colors
    status: {
      available: '#28a745',
      reserved: '#ffc107',
      maintenance: '#dc3545',
      active: '#28a745',
      pending: '#ffc107',
      completed: '#007bff',
      cancelled: '#6c757d'
    }
  },
  dark: {
    // Core colors
    background: '#0f172a',
    text: '#e2e8f0',
    cardBackground: '#1e293b',
    border: '#334155',
    primary: '#3b82f6',
    secondary: '#64748b',
    buttonText: '#ffffff',
    tableHeader: '#334155',
    inputBackground: '#1e293b',
    badgeBackground: '#374151',
    overlay: 'rgba(0, 0, 0, 0.7)',
    
    // Enhanced colors
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#06b6d4',
    
    // Responsive spacing scales
    spacing: {
      xs: '0.25rem',
      sm: '0.5rem',
      md: '1rem',
      lg: '1.5rem',
      xl: '2rem',
      xxl: '3rem'
    },
    
    // Responsive breakpoints (in pixels)
    breakpoints: {
      mobile: '768px',
      tablet: '992px',
      desktop: '1200px'
    },
    
    // Sidebar specific
    sidebar: {
      bg: '#1e293b',
      bgLighter: '#334155',
      bgDarker: '#0f172a',
      border: 'rgba(255,255,255,0.1)',
      text: '#e2e8f0',
      active: '#60a5fa',
      hover: 'rgba(96, 165, 250, 0.2)',
      width: '280px',
      mobileWidth: '260px'
    },
    
    // Footer specific
    footer: {
      bg: '#1e293b',
      border: '#334155',
      text: '#94a3b8',
      active: '#cbd5e1',
      hover: 'rgba(147, 197, 253, 0.8)',
      height: '60px',
      mobileHeight: '50px'
    },
    
    // Header specific
    header: {
      bg: '#1e293b',
      bgGradient: 'linear-gradient(90deg, #1e293b 0%, #0f172a 100%)',
      border: 'rgba(255,255,255,0.1)',
      text: '#e2e8f0',
      icon: '#60a5fa',
      height: '64px',
      mobileHeight: '56px'
    },
    
    // Chart colors
    charts: {
      primary: '#3b82f6',
      secondary: '#64748b',
      success: '#10b981',
      danger: '#ef4444',
      warning: '#f59e0b',
      info: '#06b6d4'
    },
    
    // Status colors
    status: {
      available: '#10b981',
      reserved: '#f59e0b',
      maintenance: '#ef4444',
      active: '#10b981',
      pending: '#f59e0b',
      completed: '#3b82f6',
      cancelled: '#64748b'
    }
  }
};

// Theme variants for different color schemes
const themeVariants = {
  default: {
    light: colorSchemes.light,
    dark: colorSchemes.dark
  },
  blue: {
    light: {
      ...colorSchemes.light,
      primary: '#1e40af',
      sidebar: { 
        ...colorSchemes.light.sidebar, 
        bg: '#1e3a8a',
        active: '#3b82f6'
      }
    },
    dark: {
      ...colorSchemes.dark,
      primary: '#60a5fa',
      sidebar: { 
        ...colorSchemes.dark.sidebar, 
        bg: '#1e3a8a',
        active: '#93c5fd'
      }
    }
  },
  green: {
    light: {
      ...colorSchemes.light,
      primary: '#065f46',
      sidebar: { 
        ...colorSchemes.light.sidebar, 
        bg: '#064e3b',
        active: '#10b981'
      }
    },
    dark: {
      ...colorSchemes.dark,
      primary: '#34d399',
      sidebar: { 
        ...colorSchemes.dark.sidebar, 
        bg: '#064e3b',
        active: '#6ee7b7'
      }
    }
  },
  purple: {
    light: {
      ...colorSchemes.light,
      primary: '#7c3aed',
      sidebar: { 
        ...colorSchemes.light.sidebar, 
        bg: '#5b21b6',
        active: '#8b5cf6'
      }
    },
    dark: {
      ...colorSchemes.dark,
      primary: '#a78bfa',
      sidebar: { 
        ...colorSchemes.dark.sidebar, 
        bg: '#5b21b6',
        active: '#c4b5fd'
      }
    }
  }
};

// themeVariants never changes at runtime — compute this once instead of a
// fresh array every render (it's part of the memoized context value below).
const THEME_VARIANT_NAMES = Object.keys(themeVariants);

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const { settings, updateSettings } = useSettings();
  
  // Initialize theme with system preference detection
  const [theme, setTheme] = useState(() => {
    // Check settings first
    if (settings.darkMode !== undefined) {
      return settings.darkMode ? 'dark' : 'light';
    }
    
    // Check localStorage
    const savedTheme = localStorage.getItem('app-theme');
    if (savedTheme) return savedTheme;
    
    // Auto-detect system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    
    return 'light';
  });

  // Device type detection
  const [deviceType, setDeviceType] = useState('desktop');
  
  // Get theme variant from settings or default
  const themeVariant = settings.themeVariant || 'default';

  // Get current color scheme based on theme and variant
  const colors = themeVariants[themeVariant]?.[theme] || colorSchemes[theme];

  // Detect device type
  useEffect(() => {
    const checkDeviceType = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setDeviceType('mobile');
      } else if (width < 992) {
        setDeviceType('tablet');
      } else {
        setDeviceType('desktop');
      }
    };

    checkDeviceType();
    window.addEventListener('resize', checkDeviceType);
    
    return () => window.removeEventListener('resize', checkDeviceType);
  }, []);

  // Apply theme to document and sync with settings
  useEffect(() => {
    // Set data attributes — design-system.css handles all variable values via selectors
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.setAttribute('data-theme-variant', themeVariant);
    document.documentElement.setAttribute('data-device', deviceType);

    // Only override dimension variables that depend on runtime state
    document.documentElement.style.setProperty('--sidebar-width', tokens.sidebar.width);
    document.documentElement.style.setProperty('--header-height', tokens.header.height);
    document.documentElement.style.setProperty('--header-mobile-height', tokens.header.mobileHeight);

    // Save to localStorage
    localStorage.setItem('app-theme', theme);
    localStorage.setItem('app-theme-variant', themeVariant);

    // Sync with settings if different
    const shouldBeDark = theme === 'dark';
    if (settings.darkMode !== shouldBeDark) {
      updateSettings({ darkMode: shouldBeDark });
    }
  }, [theme, themeVariant, settings.darkMode, updateSettings, deviceType]);

  // Enhanced theme toggle with animation support
  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);

    // Add animation class for smooth transition
    document.documentElement.classList.add('theme-transition');
    setTimeout(() => {
      document.documentElement.classList.remove('theme-transition');
    }, 300);
  }, [theme]);

  // Set specific theme
  const setSpecificTheme = useCallback((newTheme) => {
    if (newTheme === 'light' || newTheme === 'dark') {
      setTheme(newTheme);
    }
  }, []);

  // Change theme variant
  const setThemeVariant = useCallback((variant) => {
    if (themeVariants[variant]) {
      updateSettings({ themeVariant: variant });
    }
  }, [updateSettings]);

  // Check if current theme is dark
  const isDark = theme === 'dark';

  // Get theme information
  const getThemeInfo = useCallback(() => ({
    current: theme,
    variant: themeVariant,
    isDark,
    colors,
    deviceType,
    variants: Object.keys(themeVariants)
  }), [theme, themeVariant, isDark, colors, deviceType]);

  // Reset to default theme
  const resetTheme = useCallback(() => {
    setTheme('light');
    setThemeVariant('default');
  }, [setThemeVariant]);

  // Auto-detect system preference
  const useSystemTheme = useCallback(() => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setTheme('dark');
    } else {
      setTheme('light');
    }
    updateSettings({ themeAutoDetect: true });
  }, [updateSettings]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = (e) => {
      if (settings.themeAutoDetect) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, [settings.themeAutoDetect]);

  // Responsive utility functions — memoized because it's included in the
  // context value below; a fresh object here every render would make
  // memoizing that value pointless (its dependency would always look new).
  const responsive = useMemo(() => ({
    // Check device type
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    isDesktop: deviceType === 'desktop',

    // Responsive spacing
    spacing: (size = 'md') => colors.spacing[size] || '1rem',

    // Responsive breakpoint checks
    breakpoint: (size) => {
      const bp = { mobile: `${bpValues.mobile}px`, tablet: `${bpValues.tablet}px`, desktop: `${bpValues.desktop}px` };
      return bp[size] || '992px';
    },

    // Device-specific values
    value: (mobile, tablet, desktop) => {
      switch (deviceType) {
        case 'mobile': return mobile;
        case 'tablet': return tablet;
        default: return desktop;
      }
    }
  }), [deviceType, colors]);

  const value = useMemo(() => ({
    // Current state
    theme,
    isDark,
    colors,
    themeVariant,
    deviceType,
    responsive,

    // Actions
    toggleTheme,
    setTheme: setSpecificTheme,
    setThemeVariant,
    resetTheme,
    useSystemTheme,

    // Information
    getThemeInfo,

    // Constants
    variants: THEME_VARIANT_NAMES,
    allColors: colorSchemes
  }), [
    theme,
    isDark,
    colors,
    themeVariant,
    deviceType,
    responsive,
    toggleTheme,
    setSpecificTheme,
    setThemeVariant,
    resetTheme,
    useSystemTheme,
    getThemeInfo
  ]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Enhanced hook for theme-related CSS classes with responsive support
export const useThemeClasses = () => {
  const { theme, colors, responsive } = useTheme();
  
  return {
    // Background classes
    bgClass: `bg-${theme === 'dark' ? 'dark' : 'light'}`,
    textClass: `text-${theme === 'dark' ? 'light' : 'dark'}`,
    
    // Card classes
    cardClass: `card card-${theme} ${responsive.isMobile ? 'card-mobile' : ''}`,
    
    // Button classes
    primaryButton: `btn btn-${theme === 'dark' ? 'outline-light' : 'primary'}`,
    secondaryButton: `btn btn-${theme === 'dark' ? 'outline-secondary' : 'secondary'}`,
    
    // Table classes
    tableClass: `table table-${theme === 'dark' ? 'dark' : 'light'} ${responsive.isMobile ? 'table-responsive' : ''}`,
    
    // Responsive container
    containerClass: responsive.isMobile ? 'container-fluid' : 'container',
    
    // Responsive text sizes
    textSize: {
      small: responsive.isMobile ? 'text-sm' : 'text-base',
      medium: responsive.isMobile ? 'text-base' : 'text-lg',
      large: responsive.isMobile ? 'text-lg' : 'text-xl'
    },
    
    // Custom CSS properties for inline styles
    customProperties: colors,
    
    // Responsive utility
    responsive
  };
};

// HOC for theme-dependent components
export const withTheme = (Component) => {
  return function ThemeComponent(props) {
    const theme = useTheme();
    return <Component {...props} theme={theme} />;
  };
};

// Hook for responsive design
export const useResponsive = () => {
  const { responsive } = useTheme();
  return responsive;
};

// Hook for device detection
export const useDevice = () => {
  const { deviceType, responsive } = useTheme();
  return { deviceType, ...responsive };
};